import React from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Info,
  Language,
  School,
  TrendingUp,
  Description,
  Map,
  Group,
  AttachMoney,
} from "@mui/icons-material";

import logo from "../assets/Foundation_Lachezar_Tsotsorkov_Logo_BG_Blue.png";

const AboutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery("(max-width:600px)");

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Главно заглавие с акцент за Лъчезар Цоцорков */}
      <Box
        sx={{
          mb: { xs: 4, md: 5 },
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          bgcolor: "rgba(33, 150, 243, 0.05)",
          border: "1px solid rgba(33, 150, 243, 0.2)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center", // Променено на center за вертикално центриране
          justifyContent: "center",
          gap: { xs: 2, md: 3 },
          textAlign: { xs: "center", md: "left" }, // Добавено
        }}
      >
        {/* Лого */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={logo}
            alt="Фондация Лъчезар Цоцорков"
            style={{
              width: isSmall ? "180px" : isMobile ? "220px" : "280px", // По-малки размери за PNG лого
              height: "auto",
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        </Box>

        {/* Текст */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#455a64",
              lineHeight: 1.6,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              textAlign: "justify",
            }}
          >
            Проектът „Изследване-мапинг на читалищната мрежа в България" е
            съфинансиран от фондация „Лъчезар Цоцорков". Изявленията и мненията,
            изразени тук, принадлежат единствено на фондация „Народни читалища"
            и не отразяват непременно вижданията на фондация „Лъчезар Цоцорков"
            или нейните партньори.
          </Typography>
        </Box>
      </Box>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: 600, color: theme.palette.primary.main }}
        >
          Общински барометър на читалищата
        </Typography>

        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 500, color: theme.palette.text.secondary }}
        >
          Данни за капацитета, персонала и финансирането на читалищната мрежа
        </Typography>
      </Paper>

      {/* Основен текст - преработен */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Info color="primary" />
              За проекта
            </Typography>

            <Typography paragraph sx={{ lineHeight: 1.8 }}>
              <strong>Общинският барометър на читалищата (Барометърът)</strong>{" "}
              съдържа обобщени данни за капацитета, персонала и финансирането на
              читалищната мрежа във всяка от 265-те общини в България.
            </Typography>

            <Typography paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
              Барометърът е създаден в рамките на проект „Изследване-мапиране на
              читалищната мрежа в България", съфинансиран от фондация „Лъчезар
              Цоцорков". Изявленията и мненията, изразени тук, принадлежат
              единствено на фондация „Народни читалища" и не отразяват
              непременно вижданията на фондация „Лъчезар Цоцорков" или нейните
              партньори.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Language color="primary" />
              <Typography variant="h6">Предназначение</Typography>
            </Box>
            <Typography paragraph>
              Общинският барометър на читалищата е предназначен за институции,
              изследователи, медии, за самите читалища и за широката
              общественост. Той създава основа за по-добра видимост, информирани
              решения и бъдещо надграждане с актуални данни и нови
              функционалности.
            </Typography>

            <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
              <Map color="primary" />
              <Typography variant="h6">Допълнителни ресурси</Typography>
            </Box>
            <Typography paragraph>
              В допълнение, подробна информация за разположението, контактите и
              дейностите на всички читалища в страната може да бъде намерена на{" "}
              <Link
                to={"https://karta.narodnichitalishta.bg/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Първата онлайн карта на читалищата в България
              </Link>
              .
            </Typography>
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <TrendingUp color="primary" />
              <Typography variant="h6">Цели на проекта</Typography>
            </Box>
            <List>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <TrendingUp />
                </ListItemIcon>
                <ListItemText
                  primary="Повишаване на прозрачността на данните"
                  secondary="Направете информацията за читалищата достъпна за всички"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <Language />
                </ListItemIcon>
                <ListItemText
                  primary="Подобряване на управлението"
                  secondary="Осигуряване на данни за по-добро вземане на решения"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <School />
                </ListItemIcon>
                <ListItemText
                  primary="Подкрепа на изследователската дейност"
                  secondary="Осигуряване на данни за академични и приложни изследвания"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            </List>
          </Grid> */}
        </Grid>
      </Paper>

      {/* Партньори и реализация */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Group color="primary" />
          Партньори и реализация
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              Партньорства
            </Typography>
            <List dense>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemIcon
                  sx={{ minWidth: 40, color: theme.palette.primary.main }}
                >
                  <AttachMoney />
                </ListItemIcon>
                <ListItemText
                  primary='Фондация „Лъчезар Цоцорков"'
                  secondary="Основен донор и съфинансиращ партньор"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemIcon
                  sx={{ minWidth: 40, color: theme.palette.secondary.main }}
                >
                  <Description />
                </ListItemIcon>
                <ListItemText
                  primary="Институт за пазарна икономика"
                  secondary="Партньорство за анализ и методология"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              Технологична реализация
            </Typography>
            <Typography paragraph variant="body2">
              Барометърът е реализиран благодарение на експертния труд на
              програмистите от „Лудогорие Софт":
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Typography paragraph variant="body2">
                Емилиян Кадийски и Игнат Игнатов
              </Typography>
            </Box>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 500, mt: 2 }}
            >
              Доброволен принос
            </Typography>
            <Typography variant="body2">
              Специални благодарности на Магдалена Румбалт за доброволния принос
              в проекта.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Методология и източници */}
      {/* <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Description color="primary" />
          Методология и източници на данни
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              Източници на данни
            </Typography>
            <Typography paragraph variant="body2">
              Данните в платформата са събрани от официални източници:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Национален статистически институт (НСИ)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Националната агенция по приходите (НАП)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Регистър на читалищата" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Министерство на културата" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Министерство на финансите" />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
              Обхват на данните
            </Typography>
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              }}
            >
              <Typography variant="body2" paragraph>
                Платформата анализира общо <strong>3 597 читалища</strong>{" "}
                разпределени в <strong>265 общини</strong> на територията на
                Република България.
              </Typography>
              <Typography variant="body2">
                Данните са актуализирани към 2024 г. и включват информация за
                финансиране, персонал, капацитет и дейности на читалищната
                мрежа.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper> */}
    </Container>
  );
};

export default AboutPage;
